/**
 * PORTED — RO-3-00 / #44398.
 * Origin: `excellent/app/database/sync/_client.tsx` (verbatim, minus the inert
 * `"use client"`). Zero `next/*` edges — it was already a self-fetching island.
 *
 * `sync.status` and `sync.now` are both mounted on the Hono host, so this panel
 * is live in this shell today: it polls status every 5s and the manual trigger
 * reaches the real seam.
 *
 * Reached by 5 root pages across the Database and Settings sections (RO-3-11 and
 * RO-3-09), which is why it is here and not in one of them.
 */

import { useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  StatStrip,
  type StatStripItem,
  DataTable,
  Button,
  formatRelativeTime,
} from "@excellent/ui";
import { useApi, apiCall } from "@excellent/ui/api";
import { sync, type FleetNode } from "@excellent/ui/api/contracts/sync";

const STATE_LABEL: Record<string, string> = {
  "single-player": "Single-player",
  connected: "Connected",
  degraded: "Degraded",
  offline: "Offline",
};

export function FleetSyncPanel() {
  // Poll while connected so the operator sees a live picture; the seam's own
  // loop drives convergence, this just reflects it.
  const { data, error, isLoading, mutate } = useApi(sync.status, {}, {
    refreshInterval: 5000,
  });
  const [syncing, setSyncing] = useState(false);

  async function onSyncNow() {
    setSyncing(true);
    try {
      await apiCall(sync.now, {});
      await mutate();
    } finally {
      setSyncing(false);
    }
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-6 type-subtext text-muted-foreground">
          Could not load sync status: {String(error.message ?? error)}
        </CardContent>
      </Card>
    );
  }

  const state = data?.state ?? "single-player";
  const isReplica = data?.replica ?? false;

  // Summary tiles are now the fleet-roster table's own caption band (one Card,
  // sealed by a hairline) instead of a bare 4-up grid floating above it.
  const summaryStats: StatStripItem[] = [
    {
      title: "Connection",
      value: STATE_LABEL[state] ?? state,
      description: isReplica ? "to the owned primary" : "local file only",
    },
    {
      title: "Write volume",
      value: String(data?.writeVolume ?? 0),
      description: "records written (24h)",
    },
    {
      title: "Open conflicts",
      value: String(data?.openConflicts ?? 0),
      description: "high-stakes, awaiting reconcile",
    },
    {
      title: "Devices",
      value: String(data?.nodes.length ?? 0),
      description: "in this workspace",
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {/* Status header + manual trigger */}
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div className="flex flex-col gap-1">
            <CardTitle>{STATE_LABEL[state] ?? state}</CardTitle>
            <p className="type-subtext text-muted-foreground">
              {isReplica
                ? `Embedded replica · last synced ${formatRelativeTime(data?.lastSyncedAt ?? null)} · every ${Math.round((data?.intervalMs ?? 5000) / 1000)}s`
                : "No primary configured — this install is a standalone single-player node. Set EXCELLENT_SYNC_URL to join a fleet (see docs/multi-tenant/SETUP.md)."}
            </p>
            {data?.lastError ? (
              <p className="type-subtext text-destructive">
                Primary unreachable: {data.lastError}
              </p>
            ) : null}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onSyncNow}
            disabled={!isReplica || syncing}
          >
            {syncing ? "Syncing…" : "Sync now"}
          </Button>
        </CardHeader>
      </Card>

      {/* Fleet roster — summary tiles are the table's caption band */}
      <DataTable<FleetNode>
        title="Fleet roster"
        strongHeader
        summary={<StatStrip variant="table" items={summaryStats} />}
        rowKey={(n) => `${n.userId}:${n.deviceId ?? ""}`}
        rows={data?.nodes ?? []}
        initialSort={{ key: "user", dir: "asc" }}
        empty={
          isLoading
            ? "Loading…"
            : "No teammates yet. The first person to log in becomes the workspace owner; others join by installing a node and signing in."
        }
        columns={[
          {
            key: "user",
            header: "Person",
            sort: (a, b) => a.userDisplayName.localeCompare(b.userDisplayName),
            render: (n) => (
              <div className="flex flex-col">
                <span className="text-foreground">{n.userDisplayName}</span>
                <span className="type-subtext text-muted-foreground">@{n.handle}</span>
              </div>
            ),
          },
          {
            key: "role",
            header: "Role",
            sort: (a, b) => a.role.localeCompare(b.role),
            render: (n) => n.role,
          },
          {
            key: "device",
            header: "Device",
            sort: (a, b) =>
              (a.deviceName ?? a.deviceId ?? "").localeCompare(
                b.deviceName ?? b.deviceId ?? "",
              ),
            render: (n) => n.deviceName ?? n.deviceId ?? "—",
          },
          {
            key: "paired",
            header: "Paired",
            sort: (a, b) => Number(a.paired) - Number(b.paired),
            render: (n) => (n.paired ? "yes" : "—"),
          },
          {
            key: "lastSeen",
            header: "Last seen",
            defaultDesc: true,
            sort: (a, b) => (a.lastSeenAt ?? "").localeCompare(b.lastSeenAt ?? ""),
            render: (n) => (
              <span className="tabular-nums text-muted-foreground">
                {formatRelativeTime(n.lastSeenAt)}
              </span>
            ),
          },
        ]}
      />
    </div>
  );
}
